// Granular Refresh spec §5.1. Pure field-equality primitive shared by the
// drift detector (getDriftedFields) and the refreshable-field manifest
// (refreshableFields). Deliberately Vite-free (no import.meta) so the server
// can import it directly the same way it imports other src/lib pure helpers
// — keeping ONE comparison implementation across client badges + server
// validation.
//
// Lifted verbatim (semantics-preserving) from the deepEqual that previously
// lived inside src/modules/rollouts/diffCampaignVsRow.ts so the migration in
// the Granular Refresh slice is a delegation, not a behaviour change.
//
// Comparison rules:
// - Scalars: strict equality (===) after the shared null short-circuit.
// - Arrays: ORDERED deep equality (length + per-element). Reorder counts as
//   modified — the row editor surfaces "Headlines modified" when an AM
//   reorders, which is intended (spec §7.4.5 of P2b-2).
// - Objects: structural deep equality (same keys, deep-equal values).
//
// Tight implementation sufficient for the shapes we compare (plain JSON:
// strings, numbers, booleans, nulls, arrays, objects). No Dates, Maps, Sets,
// or cycles to worry about on content fields.

export function fieldsEqual(a: unknown, b: unknown): boolean {
  if (a === b) return true;
  if (a === null || b === null) return a === b;
  if (typeof a !== typeof b) return false;
  if (typeof a !== 'object') return false;
  if (Array.isArray(a) || Array.isArray(b)) {
    if (!Array.isArray(a) || !Array.isArray(b)) return false;
    if (a.length !== b.length) return false;
    for (let i = 0; i < a.length; i++) {
      if (!fieldsEqual(a[i], b[i])) return false;
    }
    return true;
  }
  const ao = a as Record<string, unknown>;
  const bo = b as Record<string, unknown>;
  const aKeys = Object.keys(ao);
  const bKeys = Object.keys(bo);
  if (aKeys.length !== bKeys.length) return false;
  for (const k of aKeys) {
    if (!Object.prototype.hasOwnProperty.call(bo, k)) return false;
    if (!fieldsEqual(ao[k], bo[k])) return false;
  }
  return true;
}

// Normalise a possibly-null/undefined array to [] before comparison. A row
// that never had an override stores `null`; a Campaign may store `[]`. We
// want behaviourally-identical shapes to compare equal, so both sides funnel
// through this before fieldsEqual. Mirrors the old diffCampaignVsRow#normArray.
export function normArray<T>(v: T[] | null | undefined): T[] {
  return v ?? [];
}
